import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Chip,
  Dialog,
  Divider,
  IconButton,
  Link as MuiLink,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { QmAttachment, QmCrudStatus, QmRecord } from '../../api/qmCrud';
import { downloadUrlAsFile } from '../../common/download';
import { StyledButton } from '../../components';
import QmHistoryTimeline from './QmHistoryTimeline';

export type OptionItem = {
  code: string;
  label: string;
};

export type QmDetailDialogModel = {
  selectedQm: QmRecord | null;
  isDetailsLoading: boolean;
  canEdit: boolean;
  showFilteredOutNotice: boolean;
  sectionCardSx: Record<string, unknown>;
  attachments: QmAttachment[];
  isAttachmentsLoading: boolean;
  isUploadingAttachments: boolean;
  onUploadAttachments: (files: File[]) => void;
  onSelectPreview: (preview: { name: string; url: string }) => void;
  actionComment: string;
  onActionCommentChange: (value: string) => void;
  addActionComment: () => void;
  onReprintLabels: () => void;
  isReprintingLabels: boolean;
  editStatus: QmCrudStatus;
  onEditStatusChange: (status: QmCrudStatus) => void;
  editClosureCode: string;
  onEditClosureCodeChange: (value: string) => void;
  editClosureNotes: string;
  onEditClosureNotesChange: (value: string) => void;
  saveQmEdits: () => void;
  closureOptions: OptionItem[];
  reasonOptions: OptionItem[];
  getLocationLabel: (itemLocation: string | null | undefined) => string;
  statusOptions: QmCrudStatus[];
  formatStatusLabel: (status: QmCrudStatus | string | null | undefined) => string;
};

type QmDetailDialogProps = {
  open: boolean;
  onClose: () => void;
  model: QmDetailDialogModel;
};

function formatAttachmentSize(sizeBytes: number | null): string {
  if (!sizeBytes || sizeBytes <= 0) return '-';
  if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(1)} KB`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}

const ATTACHMENT_ACCEPT_TYPES = [
  'image/*',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  '.csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel'
] as const;

async function downloadAttachmentWithOriginalFileName(attachment: QmAttachment): Promise<void> {
  await downloadUrlAsFile(attachment.downloadUrl, attachment.fileName, {
    fallbackToNewTab: true
  });
}

export default function QmDetailDialog({ open, onClose, model }: QmDetailDialogProps) {
  const {
    selectedQm,
    isDetailsLoading,
    canEdit,
    showFilteredOutNotice,
    sectionCardSx,
    attachments,
    isAttachmentsLoading,
    isUploadingAttachments,
    onUploadAttachments,
    onSelectPreview,
    actionComment,
    onActionCommentChange,
    addActionComment,
    onReprintLabels,
    isReprintingLabels,
    editStatus,
    onEditStatusChange,
    editClosureCode,
    onEditClosureCodeChange,
    editClosureNotes,
    onEditClosureNotesChange,
    saveQmEdits,
    closureOptions,
    reasonOptions,
    getLocationLabel,
    statusOptions,
    formatStatusLabel
  } = model;

  const [isUploadConfirmOpen, setIsUploadConfirmOpen] = useState(false);
  const [pendingUploadFiles, setPendingUploadFiles] = useState<File[]>([]);

  const imageAttachments = useMemo(
    () => attachments.filter((attachment) => (attachment.mimeType || '').toLowerCase().startsWith('image/')),
    [attachments]
  );

  const fileAttachments = useMemo(
    () => attachments.filter((attachment) => !(attachment.mimeType || '').toLowerCase().startsWith('image/')),
    [attachments]
  );

  const shouldShowClosureFields = editStatus === 'CLOSED';

  const poLineDisplayValue = useMemo(() => {
    if (!selectedQm) return '-';

    const values = (selectedQm.poLines || [])
      .map((line) => String(line.poLine || '').trim())
      .filter((value) => value.length > 0);

    if (values.length > 0) return values.join('\n');
    return selectedQm.poLine || '-';
  }, [selectedQm]);

  const poLineIdDisplayValue = useMemo(() => {
    if (!selectedQm) return '-';

    const values = (selectedQm.poLines || [])
      .map((line) => String(line.poLineId || '').trim())
      .filter((value) => value.length > 0);

    if (values.length > 0) return values.join(', ');
    return selectedQm.poLineId || '-';
  }, [selectedQm]);

  const itemIdDisplayValue = useMemo(() => {
    if (!selectedQm) return '-';

    const values = (selectedQm.poLines || [])
      .map((line) => String(line.itemId || '').trim())
      .filter((value) => value.length > 0);

    if (values.length > 0) return values.join(', ');
    return selectedQm.itemId || '-';
  }, [selectedQm]);

  const packageCountDisplayValue = useMemo(() => {
    if (!selectedQm) return '-';
    if ((selectedQm.packages || []).length > 0) return String(selectedQm.packages.length);
    return selectedQm.packageCount?.toString() || '-';
  }, [selectedQm]);

  const packageTypeDisplayValue = useMemo(() => {
    if (!selectedQm) return '-';

    const values = (selectedQm.packages || [])
      .sort((a, b) => a.packageNumber - b.packageNumber)
      .map((pkg) => {
        const typeValue = String(pkg.packageType || '').trim() || '-';
        return `#${pkg.packageNumber}: ${typeValue}`;
      });

    if (values.length > 0) return values.join(', ');
    return selectedQm.packageType || '-';
  }, [selectedQm]);

  const renderPurchaseOrderValue = (poValue: string | null | undefined) => {
    const poNumber = String(poValue || '').trim();
    const coupaPoUrl =
      poNumber && process.env.REACT_APP_COUPA_BASE_URL ? `${process.env.REACT_APP_COUPA_BASE_URL}${poNumber}` : '';

    if (!poNumber) return '-';
    if (!coupaPoUrl) return poNumber;

    return (
      <MuiLink
        component="a"
        href={coupaPoUrl}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          fontSize: '0.82rem',
          fontWeight: 700,
          lineHeight: 1.3,
          wordBreak: 'break-word',
          color: 'primary.main',
          textDecoration: 'underline',
          textUnderlineOffset: '2px',
          '&:hover': {
            color: 'primary.dark'
          }
        }}>
        {poNumber}
        <OpenInNewIcon sx={{ fontSize: 13 }} />
      </MuiLink>
    );
  };

  const availableStatusOptions = useMemo(() => {
    if (selectedQm?.status === 'PENDING') {
      return statusOptions.filter((status) => status !== 'CREATED');
    }

    return statusOptions;
  }, [selectedQm?.status, statusOptions]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          maxHeight: { xs: '94vh', md: '90vh' }
        }
      }}>
      <Stack spacing={1.5} sx={{ p: { xs: 1.25, md: 1.75 }, overflowY: 'auto', bgcolor: '#fafbfc' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack spacing={0.6}>
            {selectedQm ? (
              <Stack direction="row" spacing={1.25} alignItems="baseline" flexWrap="wrap" useFlexGap>
                <Typography variant="h6">{`QM ${selectedQm.qmNumber}`}</Typography>
                <Typography variant="caption" color="text.secondary">
                  QM Type: {selectedQm.type || '-'}
                </Typography>
              </Stack>
            ) : (
              <Typography variant="h6">QM Details</Typography>
            )}
          </Stack>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {!selectedQm || isDetailsLoading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            {!canEdit ? (
              <Alert severity="info">Read-only role detected. Edit and comment actions are disabled.</Alert>
            ) : null}

            {showFilteredOutNotice ? (
              <Alert severity="info">
                This QM no longer matches the current table filters, but you can continue reviewing it here.
              </Alert>
            ) : null}

            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5}>
              <Stack spacing={1.25} sx={{ flex: 1.2 }}>
                <Paper variant="outlined" sx={sectionCardSx}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    QM Details
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      gap: 1
                    }}>
                    {[
                      { label: 'Reason For QM', value: selectedQm.reasonLabel || '-' },
                      {
                        label: 'SSCC / ASN',
                        value:
                          selectedQm.sscc && selectedQm.asn
                            ? `${selectedQm.sscc} / ${selectedQm.asn}`
                            : selectedQm.sscc || selectedQm.asn || '-'
                      },
                      {
                        label: 'Purchase Order',
                        value: renderPurchaseOrderValue(selectedQm.poNumber)
                      },
                      { label: 'Purchase Order Line', value: poLineDisplayValue },
                      { label: 'PO Line ID', value: poLineIdDisplayValue },
                      { label: 'Item ID', value: itemIdDisplayValue },
                      { label: 'Supplier', value: selectedQm.supplierName || '-' },
                      { label: 'Contact', value: selectedQm.contactName || '-' },
                      { label: 'Additional Details', value: selectedQm.otherInformation || '-' },
                      { label: 'Item Location', value: getLocationLabel(selectedQm.itemLocation) },
                      { label: 'Number of Packages', value: packageCountDisplayValue },
                      { label: 'Package Types', value: packageTypeDisplayValue },
                      { label: 'Description / Comments', value: selectedQm.description || '-' },
                      { label: 'Created', value: dayjs(selectedQm.createdAt).format('DD/MM/YYYY HH:mm') }
                    ]
                      .concat(
                        shouldShowClosureFields
                          ? [
                              {
                                label: 'Closure Code',
                                value: selectedQm.closureLabel || selectedQm.closureCode || '-'
                              },
                              { label: 'Closure Notes', value: selectedQm.closureNotes || '-' }
                            ]
                          : []
                      )
                      .map((item) => (
                        <Box
                          key={item.label}
                          sx={{
                            px: 1,
                            py: 0.85,
                            borderRadius: 1.25,
                            border: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: 'background.default'
                          }}>
                          <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: 'text.secondary', mb: 0.2 }}>
                            {item.label}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.82rem',
                              color: 'text.primary',
                              lineHeight: 1.3,
                              whiteSpace: 'pre-line'
                            }}>
                            {item.value}
                          </Typography>
                        </Box>
                      ))}
                  </Box>
                </Paper>

                <Paper variant="outlined" sx={sectionCardSx}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">Attachments</Typography>
                    <Chip size="small" label={`${attachments.length} files`} variant="outlined" />
                  </Stack>
                  {canEdit ? (
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 1 }}>
                      Coordinators can upload additional files here. No file quantity limit is applied.
                    </Typography>
                  ) : null}
                  {isAttachmentsLoading ? (
                    <Stack alignItems="center" justifyContent="center" sx={{ py: 3 }}>
                      <CircularProgress size={22} />
                    </Stack>
                  ) : attachments.length > 0 ? (
                    <Stack spacing={1.25}>
                      {imageAttachments.length > 0 ? (
                        <Stack direction="row" flexWrap="wrap" gap={1.1}>
                          {imageAttachments.map((attachment) => (
                            <Box
                              key={attachment.id}
                              onClick={() =>
                                onSelectPreview({ name: attachment.fileName, url: attachment.downloadUrl })
                              }
                              sx={{
                                width: { xs: 90, md: 104 },
                                height: { xs: 90, md: 104 },
                                borderRadius: 1.5,
                                overflow: 'hidden',
                                border: '1px solid',
                                borderColor: 'divider',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  borderColor: 'primary.main',
                                  transform: 'translateY(-1px)',
                                  boxShadow: 2
                                }
                              }}>
                              <Box
                                component="img"
                                src={attachment.downloadUrl}
                                alt={attachment.fileName}
                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            </Box>
                          ))}
                        </Stack>
                      ) : null}

                      {fileAttachments.length > 0 ? (
                        <Stack spacing={0.75}>
                          {fileAttachments.map((attachment) => (
                            <Stack
                              key={attachment.id}
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="space-between"
                              sx={{
                                px: 1,
                                py: 0.75,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'background.default'
                              }}>
                              <Stack spacing={0.2} sx={{ minWidth: 0 }}>
                                <Typography variant="body2" noWrap title={attachment.fileName}>
                                  {attachment.fileName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {formatAttachmentSize(attachment.sizeBytes)}
                                </Typography>
                              </Stack>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => void downloadAttachmentWithOriginalFileName(attachment)}>
                                Download
                              </Button>
                            </Stack>
                          ))}
                        </Stack>
                      ) : null}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No attachments found for this QM.
                    </Typography>
                  )}
                </Paper>
              </Stack>

              <Stack spacing={1.25} sx={{ flex: 1 }}>
                <Paper variant="outlined" sx={sectionCardSx}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Coordinator Actions
                  </Typography>
                  <Stack
                    spacing={1}
                    sx={{
                      '& .MuiInputBase-input': { fontSize: '0.86rem' },
                      '& .MuiInputLabel-root': { fontSize: '0.86rem' },
                      '& .MuiFormHelperText-root': { fontSize: '0.72rem' }
                    }}>
                    <TextField
                      size="small"
                      label="Action Comment"
                      value={actionComment}
                      onChange={(event) => onActionCommentChange(event.target.value)}
                      multiline
                      rows={2}
                      disabled={!canEdit}
                    />
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        component="label"
                        sx={{ minWidth: 130 }}
                        disabled={!canEdit || isUploadingAttachments}>
                        {isUploadingAttachments ? 'Uploading...' : 'Upload Files'}
                        <input
                          hidden
                          type="file"
                          multiple
                          accept={ATTACHMENT_ACCEPT_TYPES.join(',')}
                          onChange={(event) => {
                            const files = Array.from(event.target.files || []);
                            if (files.length > 0) {
                              setPendingUploadFiles(files);
                              setIsUploadConfirmOpen(true);
                            }
                            event.currentTarget.value = '';
                          }}
                        />
                      </Button>
                      <StyledButton
                        variant="contained"
                        onClick={addActionComment}
                        sx={{ flex: 1 }}
                        disabled={!canEdit || !actionComment.trim()}>
                        Add Action History
                      </StyledButton>
                    </Stack>

                    <Divider />

                    <TextField
                      size="small"
                      select
                      label="Status"
                      value={editStatus}
                      onChange={(event) => onEditStatusChange(event.target.value as QmCrudStatus)}
                      disabled={!canEdit || selectedQm.status === 'CLOSED'}>
                      {availableStatusOptions.map((status) => (
                        <MenuItem key={status} value={status}>
                          {formatStatusLabel(status)}
                        </MenuItem>
                      ))}
                    </TextField>

                    {shouldShowClosureFields ? (
                      <>
                        <TextField
                          size="small"
                          select
                          label="Closure Code"
                          value={editClosureCode}
                          onChange={(event) => onEditClosureCodeChange(event.target.value)}
                          disabled={!canEdit || selectedQm.status === 'CLOSED'}>
                          <MenuItem value="">None</MenuItem>
                          {closureOptions.map((item) => (
                            <MenuItem key={item.code} value={item.code}>
                              {item.label}
                            </MenuItem>
                          ))}
                        </TextField>

                        <TextField
                          size="small"
                          label="Closure Notes"
                          value={editClosureNotes}
                          onChange={(event) => onEditClosureNotesChange(event.target.value)}
                          multiline
                          rows={2}
                          disabled={!canEdit || selectedQm.status === 'CLOSED'}
                        />
                      </>
                    ) : null}
                    <StyledButton
                      variant="contained"
                      onClick={saveQmEdits}
                      disabled={!canEdit || selectedQm.status === 'CLOSED'}>
                      Save QM Changes
                    </StyledButton>
                  </Stack>
                </Paper>

                <QmHistoryTimeline
                  selectedQm={selectedQm}
                  reasonOptions={reasonOptions}
                  sectionCardSx={sectionCardSx}
                  getLocationLabel={getLocationLabel}
                  formatStatusLabel={formatStatusLabel}
                />
              </Stack>
            </Stack>

            <Divider />
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <StyledButton
                variant="outlined"
                onClick={onReprintLabels}
                disabled={!canEdit || isReprintingLabels || Number(selectedQm.packageCount || 0) <= 0}>
                {isReprintingLabels ? 'Reprinting Labels...' : 'Reprint Labels'}
              </StyledButton>
              <StyledButton variant="outlined" onClick={onClose}>
                Close
              </StyledButton>
            </Stack>
          </Stack>
        )}
      </Stack>

      <Dialog
        open={isUploadConfirmOpen}
        onClose={() => {
          setIsUploadConfirmOpen(false);
          setPendingUploadFiles([]);
        }}
        maxWidth="xs"
        fullWidth>
        <Stack spacing={1.25} sx={{ p: 2 }}>
          <Typography sx={{ fontSize: '0.96rem', fontWeight: 700 }}>Confirm Upload</Typography>
          <Typography sx={{ fontSize: '0.84rem', color: 'text.secondary' }}>
            Are you sure you want to upload {pendingUploadFiles.length} file(s)?
          </Typography>
          {pendingUploadFiles.length > 0 ? (
            <Paper
              variant="outlined"
              sx={{
                p: 1,
                maxHeight: 180,
                overflowY: 'auto',
                backgroundColor: 'background.default'
              }}>
              <Stack spacing={0.6}>
                {pendingUploadFiles.map((file) => (
                  <Stack key={`${file.name}-${file.size}-${file.lastModified}`} direction="row" spacing={1}>
                    <Typography sx={{ fontSize: '0.78rem', color: 'text.primary', flex: 1 }}>{file.name}</Typography>
                    <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      {formatAttachmentSize(file.size)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          ) : null}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <StyledButton
              variant="outlined"
              onClick={() => {
                setIsUploadConfirmOpen(false);
                setPendingUploadFiles([]);
              }}>
              Cancel
            </StyledButton>
            <StyledButton
              variant="contained"
              onClick={() => {
                const files = pendingUploadFiles;
                setIsUploadConfirmOpen(false);
                setPendingUploadFiles([]);
                onUploadAttachments(files);
              }}
              disabled={!pendingUploadFiles.length || isUploadingAttachments}>
              Upload
            </StyledButton>
          </Stack>
        </Stack>
      </Dialog>
    </Dialog>
  );
}
